import { DRONE_ASSET_ID, DRONE_CONFIG, DRONE_FLEET_CONFIG, DRONE_EVENT_TYPES, DRONE_PLANNER_ACTIONS, getDroneDefinition, isDroneId, } from "./drone.js";
export const DRONE_FLIGHT_STATES = ["AIRBORNE", "RETURNING", "LANDING", "LANDED", "CHARGING", "TAKING_OFF", "EMERGENCY_LANDING"];
export const DRONE_BEHAVIORS = [...DRONE_PLANNER_ACTIONS, "RETURN_HOME", "LAND", "CHARGE", "EMERGENCY_LAND"];
export const DRONE_JOB_RESULTS = ["accepted", "rejected", "fallback", "stale", "timeout", "malformed"];
export function packDroneSnapshot(state) {
    const p = state.position, v = state.velocity, target = state.scanner.target;
    return [state.id, round(p.x), round(p.y), round(p.z), round(v.x), round(v.y), round(v.z),
        round(state.yaw), round(state.battery), state.flightState, state.behaviorState,
        state.currentTask.id, Math.round(state.currentTask.startedAt), Math.round(state.currentTask.plannedEndAt),
        state.scanner.mode, state.targetPlayerId, target ? [round(target.x), round(target.y), round(target.z)] : null,
        state.scanner.targetPlayerId, state.plannerGeneration];
}
export function unpackDroneSnapshot(p) {
    const definition = getDroneDefinition(p[0]);
    return {
        id: p[0], assetId: DRONE_ASSET_ID, displayName: definition.displayName, home: definition.home,
        position: { x: p[1], y: p[2], z: p[3] }, velocity: { x: p[4], y: p[5], z: p[6] },
        yaw: p[7], battery: p[8], flightState: p[9], behaviorState: p[10],
        currentTask: { id: p[11], type: p[10], startedAt: p[12], plannedEndAt: p[13] },
        scanner: { mode: p[14], targetPlayerId: p[17], target: p[16] ? { x: p[16][0], y: p[16][1], z: p[16][2] } : null },
        targetPlayerId: p[15], plannerGeneration: p[18],
    };
}
export function isPackedDrone(value) {
    if (!Array.isArray(value) || value.length !== 19 || !isDroneId(value[0]))
        return false;
    const p = value;
    const home = getDroneDefinition(p[0]).home;
    return p.slice(1, 8).every((n) => finite(n, -100_000, 100_000))
        && Math.hypot(p[1] - home.x, p[3] - home.z) <= DRONE_CONFIG.hardHomeRadius + 0.002
        && p.slice(4, 7).every((n) => Math.abs(n) <= 20)
        && finite(p[8], 0, 100) && DRONE_FLIGHT_STATES.includes(p[9]) && DRONE_BEHAVIORS.includes(p[10])
        && identifier(p[11], 96) && timestamp(p[12]) && timestamp(p[13]) && p[13] >= p[12]
        && p[13] - p[12] <= 3_600_000 && ["idle", "player", "sweep"].includes(p[14])
        && playerId(p[15]) && playerId(p[17])
        && (p[16] === null || Array.isArray(p[16]) && p[16].length === 3 && p[16].every((n) => finite(n, -100_000, 100_000)))
        && (p[14] !== "player" || p[16] !== null && p[17] !== null)
        && Number.isSafeInteger(p[18]) && p[18] >= 0;
}
export function isDroneSnapshotList(value) {
    return Array.isArray(value) && value.length <= DRONE_FLEET_CONFIG.count && value.every(isPackedDrone)
        && new Set(value.map((drone) => drone[0])).size === value.length;
}
export function isServerDroneTelemetry(value) {
    if (!record(value) || !exact(value, ["t", "droneId", "at", "home", "displayName", "plannerReady", "targetDistanceMeters", "decisionSummary", "lastSpeech", "jobs"]))
        return false;
    const definition = typeof value.droneId === "string" ? getDroneDefinition(value.droneId) : undefined;
    return value.t === "drone_telemetry" && !!definition && timestamp(value.at)
        && record(value.home) && exact(value.home, ["x", "z"]) && value.home.x === definition.home.x && value.home.z === definition.home.z
        && value.displayName === definition.displayName && typeof value.plannerReady === "boolean"
        && (value.targetDistanceMeters === null || finite(value.targetDistanceMeters, 0, DRONE_CONFIG.disengageRange + 5))
        && publicText(value.decisionSummary, DRONE_CONFIG.decisionSummaryMaximumChars)
        && (value.lastSpeech === null || isDroneSpeech(value.lastSpeech))
        && Array.isArray(value.jobs) && value.jobs.length <= 8 && value.jobs.every(isDronePlannerJob);
}
export function isServerDroneShoutResult(value) {
    return record(value) && exact(value, ["t", "droneId", "requestId", "ok", "code"])
        && value.t === "drone_shout_result" && isDroneId(value.droneId) && identifier(value.requestId, 64)
        && typeof value.ok === "boolean" && ["OK", "UNKNOWN_PLAYER", "INVALID_DRONE", "INVALID_REQUEST", "TOO_FAR", "OCCLUDED", "RATE_LIMITED"].includes(String(value.code))
        && value.ok === (value.code === "OK");
}
export function isDronePlannerJob(value) {
    if (!record(value) || !exact(value, ["jobId", "event", "model", "quant", "queuedAt", "startedAt", "finishedAt", "queueMs", "wallMs", "cpuMs", "averageCpuPercent", "coreEquivalent", "threadLimit", "promptTokens", "outputTokens", "generationTokensPerSecond", "promptTokensPerSecond", "result"]))
        return false;
    return identifier(value.jobId, 96) && DRONE_EVENT_TYPES.includes(value.event)
        && publicText(value.model, 96) && publicText(value.quant, 24)
        && timestamp(value.queuedAt) && timestamp(value.startedAt) && timestamp(value.finishedAt)
        && value.startedAt >= value.queuedAt && value.finishedAt >= value.startedAt
        && finite(value.queueMs, 0, 86_400_000) && finite(value.wallMs, 0, 86_400_000)
        && [value.cpuMs, value.averageCpuPercent, value.coreEquivalent, value.generationTokensPerSecond, value.promptTokensPerSecond].every((n) => n === null || finite(n, 0, 1_000_000_000))
        && [value.promptTokens, value.outputTokens].every((n) => n === null || Number.isSafeInteger(n) && n >= 0 && n <= 8192)
        && Number.isInteger(value.threadLimit) && finite(value.threadLimit, 1, 64)
        && DRONE_JOB_RESULTS.includes(value.result);
}
function isDroneSpeech(value) {
    return record(value) && exact(value, ["id", "mode", "text", "at", "expiresAt"])
        && identifier(value.id, 96) && ["speech", "opaque_transmission"].includes(String(value.mode))
        && publicText(value.text, DRONE_CONFIG.speechMaximumChars) && timestamp(value.at) && timestamp(value.expiresAt)
        && value.expiresAt >= value.at && value.expiresAt - value.at <= 60_000;
}
function publicText(value, max) {
    return typeof value === "string" && value.length <= max && !/[\u0000-\u001f\u007f-\u009f<>]/u.test(value);
}
function record(value) { return !!value && typeof value === "object" && !Array.isArray(value); }
function exact(value, keys) { return Object.keys(value).length === keys.length && keys.every((key) => Object.hasOwn(value, key)); }
function finite(value, min, max) { return typeof value === "number" && Number.isFinite(value) && value >= min && value <= max; }
function timestamp(value) { return Number.isSafeInteger(value) && value >= 0; }
function identifier(value, max) { return typeof value === "string" && value.length > 0 && value.length <= max && /^[A-Za-z0-9:_-]+$/.test(value); }
function playerId(value) { return value === null || identifier(value, 64); }
function round(n) { return Math.round(n * 1000) / 1000; }
